import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Option } from './Option'
import { User } from './User'

type TaxcodeConstructorArgs = ConstructorArgs<() => Taxcode>

export class Taxcode extends BaseRecord {
    readonly recordType = 'taxcode'

    name?: string
    memo?: string
    rate?: number
    city?: string
    street?: string
    state?: string
    postalCode?: string
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<TaxcodeConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.name = data.name ?? undefined
        this.memo = data.memo ?? undefined
        this.rate = Number(data.rate) ?? undefined
        this.city = data.city ?? undefined
        this.state = data.state ?? undefined
        this.street = data.street ?? undefined
        this.postalCode = data.postal_code ?? undefined
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<TaxcodeConstructorArgs>): Taxcode {
        return new Taxcode({ ...this.serializeForClone(), ...overrides })
    }

    static defaultTaxable(user: User): boolean {
        if (!user.allowSalesTax) return false
        if (user.alwaysCollectTax) return true
        if (
            user.state === 'KY' &&
            [
                'petGrooming',
                'petSitting',
                'dogBoarding',
                'dogTraining',
                'dogWalking',
                'fitness',
                'healthWellness',
                'landscaping',
                'homeCleaning',
            ].includes(user.industry!.id!)
        )
            return true
        return (
            user.state !== undefined &&
            ['IA', 'CT', 'HI', 'SD', 'NM', 'WV', 'CO', 'IL', 'NE', 'VA', 'OH', 'NY', 'NJ'].includes(
                user.state
            )
        )
    }

    static defaultOrderTaxable(user: User): boolean {
        if (!user.allowSalesTax) return false
        if (user.alwaysCollectTax) return true
        return Taxcode.defaultProductTaxable(user.state)
    }

    static defaultOrigin(user: User): boolean {
        return (
            user.state !== undefined &&
            ['AZ', 'CA', 'IL', 'MS', 'MO', 'NM', 'OH', 'PA', 'TN', 'TX', 'UT', 'VA'].includes(
                user.state
            )
        )
    }

    static defaultProductTaxable(state: string | undefined): boolean {
        return state !== undefined && !['OR', 'DE', 'NH', 'MT', 'AK'].includes(state)
    }

    static defaultShippingTaxable(state: string | undefined): boolean {
        return (
            state !== undefined &&
            [
                'AK',
                'CT',
                'DC',
                'GA',
                'HI',
                'IL',
                'IN',
                'IL',
                'KS',
                'KY',
                'MI',
                'MN',
                'MS',
                'NE',
                'NJ',
                'NM',
                'NY',
                'NC',
                'ND',
                'OH',
                'PA',
                'RI',
                'SC',
                'SD',
                'TN',
                'TX',
                'VT',
                'WV',
                'WI',
            ].includes(state)
        )
    }

    static taxStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: "Don't collect" }))
        strings.push(new Option(undefined, { id: 'pro', label: 'Business location' }))
        strings.push(new Option(undefined, { id: 'transaction', label: 'Transaction location' }))
        return strings
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static dataType(): DataType {
        return 'taxcodes'
    }
}

export const isShippingTaxable = (state: string) => {
    return (
        !!state &&
        [
            'AK',
            'CT',
            'DC',
            'GA',
            'HI',
            'IL',
            'IN',
            'IL',
            'KS',
            'KY',
            'MI',
            'MN',
            'MS',
            'NE',
            'NJ',
            'NM',
            'NY',
            'NC',
            'ND',
            'OH',
            'PA',
            'RI',
            'SC',
            'SD',
            'TN',
            'TX',
            'VT',
            'WV',
            'WI',
        ].includes(state)
    )
}

export const isProductTaxable = (state: string) => !['OR', 'DE', 'NH', 'MT', 'AK'].includes(state)
